import { Component, h, Prop } from '@stencil/core';
import { htmlSanitizer } from './../../../utils/html-sanitizer';

@Component({
  tag: 'vs-link',
})
export class Link {
  /**
  @shortDescription Specifies whether the CTA should look like a link or button */
  @Prop() linkStyle: string;

  /**
  @shortDescription Specifies whether the link is internal, video or external */
  @Prop() linkType: string;

  /**
  @shortDescription Specifies color of button */
  @Prop() buttonColor: string;

  /**
  @shortDescription Specifies link text */
  @Prop() linkText: string;

  /**
  @shortDescription Specifies target of the link, e.g. new, sam or modal */
  @Prop() linkTarget: string;

  /**
  @shortDescription Describes coversion for tracking purposes */
  @Prop() linkConversion: string;

  /**
  @shortDescription Specifies href value for the link */
  @Prop() linkVal: string;

  /**
  @shortDescription Specifies ara-label for the link */
  @Prop() linkAriaLabel: string;

  /**
  @shortDescription Specifies if link has dark background */
  @Prop() hasDarkBg: string;

  /**
  @shortDescription Specifies thumbnail image path for video link */
  @Prop() imgPath: string;

  /**
  @shortDescription Specifies path for YouTube video */
  @Prop() videoUrl: string;

  /**
  @shortDescription Specifies title for video */
  @Prop() videoTitle: string;

  /**
  @shortDescription Specifies unique Id that is needed for modal functioning */
  @Prop() modalTarget?: string = 'cta-modal-target';

  /**
  @shortDescription Specifies path for YouTube */
  @Prop() isNoFollow?: string;

  private classList = '';
  private videoId = '';
  private whereLinkOpens = '';

  private setButtonColorClass(colorValue: string) {
    switch (colorValue) {
      case 'primary':
        return 'vs-btn vs-btn-primary ';
      case 'secondary':
        return 'vs-btn vs-btn-secondary ';
      case 'white':
        return 'vs-btn ';
    }
  }

  private setLinkColorClass(darkBg: string) {
    if (darkBg === 'yes') {
      return 'vs-alt';
    } else return ' ';
  }

  // START GENAI@CHATGPT4
  private setButtonIconClass(linkType: string, linkTarget: string): string {
    const internalLinkClasses = {
      new: 'vs-btn-new-window ',
      same: 'vs-btn-icon ',
      modal: 'vs-btn-modal ',
    };

    if (linkType === 'internal') {
      return internalLinkClasses[linkTarget] || '';
    } else if (linkType === 'video') {
      return 'vs-btn-video ';
    } else {
      return linkTarget === 'new' ? 'vs-btn-new-window ' : 'vs-btn-share ';
    }
  }
  // END GENAI@CHATGPT4

  // START GENAI@CHATGPT4
  private setLinkIconClass(linkType: string, linkTarget: string) {
    const linkClassMap = {
      internal: {
        new: 'vs-link-new-window ',
        same: 'vs-link-secondary ',
        modal: 'vs-link-modal ',
      },
      video: 'vs-link-video ',
      default: {
        new: 'vs-link-new-window ',
        default: 'vs-link-outside ',
      },
    };

    let linkClass = linkClassMap[linkType];

    if (typeof linkClass === 'object') {
      return linkClass[linkTarget] || linkClass['default'];
    }

    return linkClass || linkClassMap['default'][linkTarget] || linkClassMap['default']['default'];
  }
  // END GENAI@CHATGPT4

  private setModalShowClass(linkType: string, linkTarget: string) {
    if (linkTarget === 'modal' || linkType === 'video') {
      return 'modal__show';
    } else return ' ';
  }

  private setTargetForAnchor(linkType: string, linkTarget: string) {
    let target = '';
    if (linkType !== 'video' || linkTarget !== 'modal') {
      switch (linkTarget) {
        case 'new':
          target = '_blank';
          break;
        case 'same':
          target = '_self';
          break;
      }
      return target;
    }
  }

  componentWillLoad() {
    this.renderLink();
  }

  componentWillUpdate() {
    this.renderLink();
  }

  private renderLink() {
    if (this.linkStyle === 'link') {
      this.classList = 'vs-link-cta ' + this.setLinkColorClass(this.hasDarkBg)+ ' ' + this.setLinkIconClass(this.linkType, this.linkTarget);
    } else {
      this.classList = this.setButtonColorClass(this.buttonColor) + this.setButtonIconClass(this.linkType, this.linkTarget);
    }
    this.classList += this.setModalShowClass(this.linkType, this.linkTarget);
    if (this.linkType === 'video') {
      this.videoId = this.videoUrl?.split('watch?v=')[1];
    }
    this.whereLinkOpens = this.setTargetForAnchor(this.linkType, this.linkTarget);
  }

  private getVideoLinkAttributes() {
    return {
      'data-image': this.imgPath,
      'data-title': this.videoTitle,
      'data-videoid': this.videoId,
      'data-modaltype': 'video',
      'tabindex': '0',
    };
  }

  render() {
    return (
      <a
        class={this.classList}
        innerHTML={htmlSanitizer(this.linkText)}
        aria-label={this.linkAriaLabel?.length > 0 ? this.linkText + ' ' + this.linkAriaLabel : this.linkText}
        {...(this.linkType === 'internal' && this.linkTarget === 'modal' ? { 'data-modaltype': 'internal' } : {})}
        {...(this.linkType === 'video' || this.linkTarget === 'modal'
          ? { 'role': 'button', 'data-target': this.modalTarget }
          : {
            'target': this.whereLinkOpens,
            ...(this.linkType === 'external' ? { 'data-conversiontype': this.linkConversion } : {})  // This line conditionally adds data-conversiontype
          }
        )}
        {...(this.linkType !== 'video'
          ? { href: this.linkVal }
          : {
            ...this.getVideoLinkAttributes(),
            'data-target': this.modalTarget,
          })}
        {...(this.linkType === 'external' && this.isNoFollow ? { rel: this.isNoFollow } : '')}
      ></a>
    );
  }
}
